import { useMemo } from 'react'

import type { Survey, SurveyFilters } from 'app/common/types/surveys'
import { useSurveyFilterOptions } from 'app/dataProviders/surveys/hooks'

import {
  CityFilter,
  DistrictFilter,
  PeriodFilter,
  SchoolFilter,
  SchoolTypeFilter,
} from './filterItems'
import PlainSelectorStatic from 'app/common/ui-next/plain-selector/plain-selector-static'
import styles from './Filters.module.scss'

export const emptySurveyFilters: SurveyFilters = {
  q: '',
  cityIds: [],
  districtIds: [],
  schoolTypes: [],
  schoolIds: [],
  from: null,
  to: null,
}

type Props = {
  creation?: boolean
  scope?: Survey
  value: SurveyFilters
  onChange: (value: SurveyFilters) => void
  hidePeriod?: boolean
  periodDisabled?: boolean
  multiple?: boolean
  grayBackground?: boolean
}

export const Filters = ({
  value,
  creation,
  scope,
  onChange,
  hidePeriod,
  periodDisabled,
  multiple = true,
  grayBackground,
}: Props) => {
  const scopeIds = useMemo(() => {
    const ids = (values?: Array<string>, single?: string) =>
      values?.length ? values : single ? [single] : []
    return {
      cityIds: ids(scope?.scope.cityIds, scope?.scope.cityId),
      districtIds: ids(scope?.scope.districtIds, scope?.scope.districtId),
      schoolIds: ids(scope?.scope.schoolIds, scope?.scope.schoolId),
    }
  }, [scope])
  const requestParams = useMemo(
    () => ({
      cityIds: value.cityIds.length ? value.cityIds : scopeIds.cityIds,
      districtIds: value.districtIds.length ? value.districtIds : scopeIds.districtIds,
      schoolTypes: scope?.scope.schoolType ? [scope.scope.schoolType] : value.schoolTypes,
    }),
    [value.cityIds, value.districtIds, value.schoolTypes, scope, scopeIds],
  )
  const { data: availableOptions, loading } = useSurveyFilterOptions({ params: requestParams })
  const options = useMemo(() => {
    if (!scope) return availableOptions
    const restrict = <T extends { id: string }>(items: Array<T>, ids?: Array<string>) =>
      ids?.length ? items.filter((item) => ids.includes(item.id)) : items
    const schools = restrict(availableOptions.schools, scopeIds.schoolIds).filter(
      (item) =>
        !scopeIds.districtIds.length || scopeIds.districtIds.includes(item.districtId || ''),
    )
    const scopedSchools = [...(scope.schools?.items || []), ...schools].filter((item) =>
      scopeIds.schoolIds.includes(item.id),
    )
    const schoolDistrictIds = new Set(scopedSchools.map((item) => item.districtId).filter(Boolean))
    const districts = restrict(availableOptions.districts, scopeIds.districtIds)
      .filter((item) => !scopeIds.schoolIds.length || schoolDistrictIds.has(item.id))
    const cities = restrict(availableOptions.cities, scopeIds.cityIds)
    return {
      ...availableOptions,
      cities,
      districts,
      schools,
      schoolTypes: scope.scope.schoolType
        ? availableOptions.schoolTypes.filter((item) => item.id === scope.scope.schoolType)
        : availableOptions.schoolTypes,
    }
  }, [availableOptions, scope, scopeIds])
  const colorInverse = Boolean(grayBackground)
  const handleChange = (nextValue: SurveyFilters) => onChange({
    ...nextValue,
    cityIds: scopeIds.cityIds.length ? scopeIds.cityIds : nextValue.cityIds,
    districtIds: scopeIds.districtIds.length ? scopeIds.districtIds : nextValue.districtIds,
    schoolIds: scopeIds.schoolIds.length ? scopeIds.schoolIds : nextValue.schoolIds,
    schoolTypes: scope?.scope.schoolType ? [scope.scope.schoolType] : nextValue.schoolTypes,
  })
  const filterProps = {
    value,
    creation,
    options,
    loading,
    multiple,
    colorInverse,
    onChange: handleChange,
  }

  return (
    <div
      className={`${styles.root} ${hidePeriod ? styles.noPeriod : ''} ${creation ? styles.creation : ''} ${grayBackground ? styles.grayBackground : ''}`}
    >
      {!hidePeriod && (
        <div className={styles.periodColumn}>
          <PeriodFilter value={value} onChange={onChange} disabled={periodDisabled} />
        </div>
      )}
      <div className={styles.column}>
        {creation && (
          <>
            <PlainSelectorStatic
              label="Вид опроса"
              id="food_quality"
              title={options.surveyKindLabel || '—'}
              disabled
              skipUpperSpace
            />
            <PlainSelectorStatic
              label="Место опроса"
              id="self_service_terminals"
              title={options.surveyLocationLabel || '—'}
              disabled
              skipUpperSpace
            />
          </>
        )}
        <SchoolTypeFilter
          {...filterProps}
          multiple={creation ? false : multiple}
          disabled={Boolean(scope?.scope.schoolType)}
        />
      </div>
      <div className={styles.column}>
        <CityFilter {...filterProps} disabled={scopeIds.cityIds.length > 0} />
        {(!creation || value.cityIds.length === 1) && (
          <>
            <DistrictFilter {...filterProps} disabled={scopeIds.districtIds.length > 0} />
            <SchoolFilter {...filterProps} disabled={scopeIds.schoolIds.length > 0} />
          </>
        )}
      </div>
    </div>
  )
}
